import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";

const BACKEND_URL = window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function useAdminPanel({ onLogout }) {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [emails, setEmails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newEmailLifetime, setNewEmailLifetime] = useState("");
  const [customAddress, setCustomAddress] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [expandedEmail, setExpandedEmail] = useState(null);
  const [emailSearchQuery, setEmailSearchQuery] = useState("");

  // Settings state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [adminUsername, setAdminUsername] = useState("");
  const [adminRole, setAdminRole] = useState("super_admin");
  const [adminPermissions, setAdminPermissions] = useState({});

  // Email viewer state
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailMessages, setEmailMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);

  // Site config state
  const [siteSettings, setSiteSettings] = useState(null);
  const [editSiteName, setEditSiteName] = useState("");
  const [editDurations, setEditDurations] = useState([]);
  const [editDefaultDuration, setEditDefaultDuration] = useState("");
  const [editAllowPermanent, setEditAllowPermanent] = useState(false);
  const [editAllowCustomAddress, setEditAllowCustomAddress] = useState(false);
  const [editMaintenanceMode, setEditMaintenanceMode] = useState(false);
  const [editAdminTitle, setEditAdminTitle] = useState("Admin");
  const [editAccentColor, setEditAccentColor] = useState("#00f0ff");
  const [editWelcomeText, setEditWelcomeText] = useState("");
  const [editFooterText, setEditFooterText] = useState("");
  const [editDomains, setEditDomains] = useState([]);
  const [isSavingConfig, setIsSavingConfig] = useState(false);

  // Sessions state
  const [sessions, setSessions] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [cronJobs, setCronJobs] = useState([]);
  const [cronEvents, setCronEvents] = useState([]);
  const [isLoadingCron, setIsLoadingCron] = useState(false);
  const [runningJobId, setRunningJobId] = useState(null);

  // Users state
  const [users, setUsers] = useState([]);
  const [usersStats, setUsersStats] = useState(null);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  const token = localStorage.getItem("admin_token");

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      if (error.response?.status === 401) onLogout();
    }
  }, [token, onLogout]);

  const fetchEmails = useCallback(async (search) => {
    setIsLoading(true);
    try {
      const params = {};
      const q = search !== undefined ? search : emailSearchQuery;
      if (q) params.search = q;
      const response = await axios.get(`${API}/admin/emails`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setEmails(response.data.emails);
    } catch (error) {
      if (error.response?.status === 401) onLogout();
    } finally {
      setIsLoading(false);
    }
  }, [token, onLogout, emailSearchQuery]);

  const [adminAvatar, setAdminAvatar] = useState(null);

  const fetchProfile = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminUsername(response.data.username);
      setAdminAvatar(response.data.avatar || null);
      setAdminRole(response.data.role || "super_admin");
      setAdminPermissions(response.data.permissions || {});
    } catch (error) {
      console.error("Failed to fetch profile");
    }
  }, [token]);

  const uploadAvatar = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          await axios.post(`${API}/admin/avatar`, { avatar: reader.result }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setAdminAvatar(reader.result);
          toast.success("Avatar mis à jour");
          resolve();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Erreur lors de l'upload");
          reject(error);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const deleteAvatar = async () => {
    try {
      await axios.delete(`${API}/admin/avatar`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminAvatar(null);
      toast.success("Avatar supprimé");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const fetchSiteSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/site-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSiteSettings(response.data);
      setEditSiteName(response.data.site_name);
      setEditDurations((response.data.durations || []).map(d => ({ ...d, enabled: d.enabled !== false })));
      setEditDefaultDuration(response.data.default_duration);
      // Set default lifetime for create modal
      if (!newEmailLifetime) {
        setNewEmailLifetime(response.data.default_duration || response.data.durations?.[0]?.key || "10min");
      }
      setEditAllowPermanent(response.data.allow_permanent || false);
      setEditAllowCustomAddress(response.data.allow_custom_address || false);
      setEditMaintenanceMode(response.data.maintenance_mode || false);
      setEditAdminTitle(response.data.admin_title || "Admin");
      setEditAccentColor(response.data.accent_color || "#00f0ff");
      setEditWelcomeText(response.data.welcome_text || "");
      setEditFooterText(response.data.footer_text || "");
      const allDomains = response.data.domains || [];
      setEditDomains(allDomains.length > 1 ? allDomains.slice(1) : []);
    } catch (error) {
      console.error("Failed to fetch site settings");
    }
    // newEmailLifetime sert uniquement à l'initialisation (on ne l'écrase pas
    // s'il est déjà défini) : l'ajouter recréerait ce callback à chaque choix.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Users management
  const fetchUsers = useCallback(async () => {
    setIsLoadingUsers(true);
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      if (error.response?.status === 401) onLogout();
    } finally {
      setIsLoadingUsers(false);
    }
  }, [token, onLogout]);

  const fetchUsersStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/users/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsersStats(response.data);
    } catch (error) {
      // silent
    }
  }, [token]);

  useEffect(() => {
    fetchStats();
    fetchEmails();
    fetchProfile();
    fetchSiteSettings();
    fetchUsers();
  }, [fetchStats, fetchEmails, fetchProfile, fetchSiteSettings, fetchUsers]);

  const createEmail = async () => {
    setIsCreating(true);
    try {
      const response = await axios.post(
        `${API}/admin/emails/generate`,
        { lifetime: newEmailLifetime, custom_address: customAddress || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Email créé", { description: response.data.full_address });
      setIsCreateModalOpen(false);
      setCustomAddress("");
      fetchStats();
      fetchEmails();
    } catch (error) {
      toast.error("Erreur lors de la création", {
        description: error.response?.data?.detail || "Erreur inconnue"
      });
    } finally {
      setIsCreating(false);
    }
  };

  const deleteEmail = async (emailId) => {
    try {
      await axios.delete(`${API}/admin/emails/${emailId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Email supprimé");
      if (selectedEmail?.id === emailId) {
        setSelectedEmail(null);
        setEmailMessages([]);
      }
      fetchStats();
      fetchEmails();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const deleteEmailsBulk = async (emailIds) => {
    try {
      const response = await axios.post(
        `${API}/admin/emails/delete-bulk`,
        { email_ids: emailIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${response.data.deleted} email(s) supprimé(s)`);
      if (selectedEmail && emailIds.includes(selectedEmail.id)) {
        setSelectedEmail(null);
        setEmailMessages([]);
      }
      fetchStats();
      fetchEmails();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const deleteAllEmails = async () => {
    try {
      const response = await axios.delete(`${API}/admin/emails/delete-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${response.data.deleted} email(s) supprimé(s)`);
      setSelectedEmail(null);
      setEmailMessages([]);
      fetchStats();
      fetchEmails();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const extendEmail = async (emailId, extendBy) => {
    try {
      const response = await axios.post(
        `${API}/admin/emails/${emailId}/extend`,
        { extend_by: extendBy },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchEmails();
      fetchStats();
    } catch (error) {
      toast.error("Erreur lors de la prolongation", {
        description: error.response?.data?.detail || "Erreur inconnue"
      });
    }
  };

  const expireEmail = async (emailId) => {
    try {
      const response = await axios.post(
        `${API}/admin/emails/${emailId}/expire`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchEmails();
      fetchStats();
    } catch (error) {
      toast.error("Erreur lors de l'expiration", {
        description: error.response?.data?.detail || "Erreur inconnue"
      });
    }
  };

  const togglePinEmail = async (emailId, currentPinned) => {
    try {
      const response = await axios.patch(
        `${API}/admin/emails/${emailId}/pin`,
        { pinned: !currentPinned },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchEmails();
    } catch (error) {
      toast.error("Erreur lors de l'épinglage");
    }
  };

  const toggleReplyEmail = async (emailId, currentReplyEnabled) => {
    try {
      const response = await axios.patch(
        `${API}/admin/emails/${emailId}/reply-toggle`,
        { reply_enabled: !currentReplyEnabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchEmails();
    } catch (error) {
      toast.error("Erreur lors du changement de réponse");
    }
  };

  const fetchEmailMessages = useCallback(async (emailId, silent = false) => {
    if (!silent) setIsLoadingMessages(true);
    try {
      const response = await axios.get(`${API}/admin/emails/${emailId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailMessages(response.data);
    } catch (error) {
      if (error.response?.status === 401) onLogout();
      else if (!silent) toast.error("Erreur lors du chargement des messages");
      if (!silent) setEmailMessages([]);
    } finally {
      if (!silent) setIsLoadingMessages(false);
    }
  }, [token, onLogout]);

  const fetchMessage = async (messageId) => {
    try {
      const response = await axios.get(`${API}/admin/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedMessage(response.data);
      setIsMessageModalOpen(true);
      if (selectedEmail) fetchEmailMessages(selectedEmail.id, true);
    } catch (error) {
      if (error.response?.status === 401) onLogout();
      else toast.error("Erreur lors du chargement du message");
    }
  };

  const openEmailInbox = (email) => {
    setSelectedEmail(email);
    fetchEmailMessages(email.id);
  };

  const closeEmailInbox = () => {
    setSelectedEmail(null);
    setEmailMessages([]);
  };

  const saveCredentials = async () => {
    if (newPassword && newPassword !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    if (!currentPassword) {
      toast.error("Veuillez entrer votre mot de passe actuel");
      return;
    }
    setIsSaving(true);
    try {
      const response = await axios.post(
        `${API}/admin/change-credentials`,
        { current_password: currentPassword, new_username: newUsername || null, new_password: newPassword || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Paramètres sauvegardés", { description: `Nom d'utilisateur: ${response.data.username}` });
      setAdminUsername(response.data.username);
      setCurrentPassword("");
      setNewUsername("");
      setNewPassword("");
      setConfirmPassword("");
      if (newUsername && newUsername !== adminUsername) {
        toast.info("Veuillez vous reconnecter avec vos nouveaux identifiants");
        setTimeout(onLogout, 2000);
      }
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde", {
        description: error.response?.data?.detail || "Vérifiez votre mot de passe actuel"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const saveSiteSettings = async () => {
    if (!editSiteName.trim()) {
      toast.error("Le nom du site ne peut pas être vide");
      return;
    }
    if (editDurations.length === 0) {
      toast.error("Il faut au moins une durée");
      return;
    }
    setIsSavingConfig(true);
    try {
      await axios.post(
        `${API}/admin/site-settings`,
        {
          site_name: editSiteName, durations: editDurations,
          default_duration: editDefaultDuration, allow_permanent: editAllowPermanent,
          allow_custom_address: editAllowCustomAddress, maintenance_mode: editMaintenanceMode,
          admin_title: editAdminTitle,
          accent_color: editAccentColor, welcome_text: editWelcomeText, footer_text: editFooterText,
          domains: [siteSettings?.domains?.[0] || "shadowmail.cc", ...editDomains]
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Configuration sauvegardée");
      fetchSiteSettings();
    } catch (error) {
      toast.error("Erreur", { description: error.response?.data?.detail || "Erreur inconnue" });
    } finally {
      setIsSavingConfig(false);
    }
  };

  const addDuration = () => {
    const num = editDurations.length + 1;
    setEditDurations([...editDurations, { key: "", label: "", minutes: 0, enabled: true }]);
  };

  const removeDuration = (index) => {
    const updated = editDurations.filter((_, i) => i !== index);
    setEditDurations(updated);
    if (editDefaultDuration === editDurations[index]?.key) {
      setEditDefaultDuration(updated[0]?.key || "");
    }
  };

  const updateDuration = (index, field, value) => {
    const updated = [...editDurations];
    updated[index] = { ...updated[index], [field]: field === "minutes" ? Number(value) : value };
    if (field === "minutes") {
      const mins = Number(value);
      if (mins > 0) {
        if (mins < 60) updated[index].key = `${mins}min`;
        else if (mins < 1440) updated[index].key = `${Math.round(mins / 60)}h`;
        else updated[index].key = `${Math.round(mins / 1440)}j`;
        if (!updated[index].label) {
          if (mins < 60) updated[index].label = `${mins} minutes`;
          else if (mins < 1440) updated[index].label = `${Math.round(mins / 60)} heure(s)`;
          else updated[index].label = `${Math.round(mins / 1440)} jour(s)`;
        }
      }
    }
    setEditDurations(updated);
  };

  // Sessions management
  const fetchSessions = useCallback(async () => {
    setIsLoadingSessions(true);
    try {
      const response = await axios.get(`${API}/admin/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(response.data);
    } catch (error) {
      console.error("Failed to fetch sessions");
    } finally {
      setIsLoadingSessions(false);
    }
  }, [token]);

  const fetchCron = useCallback(async (silent = false) => {
    if (!silent) setIsLoadingCron(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [jobsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/admin/cron/jobs`, { headers }),
        axios.get(`${API}/admin/cron/events?limit=50`, { headers }),
      ]);
      setCronJobs(jobsRes.data);
      setCronEvents(eventsRes.data);
    } catch (error) {
      console.error("Failed to fetch cron data");
    } finally {
      if (!silent) setIsLoadingCron(false);
    }
  }, [token]);

  const updateCronJob = async (jobId, updates) => {
    // Mise à jour optimiste pour que l'interface réagisse tout de suite
    setCronJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, ...updates } : j)));
    try {
      await axios.patch(`${API}/admin/cron/jobs/${jobId}`, updates, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchCron(true);
      return true;
    } catch (error) {
      toast.error("Erreur", { description: error.response?.data?.detail || "Erreur inconnue" });
      fetchCron(true);
      return false;
    }
  };

  const runCronJob = async (jobId) => {
    setRunningJobId(jobId);
    try {
      const res = await axios.post(`${API}/admin/cron/jobs/${jobId}/run`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const ev = res.data;
      if (ev.status === "success") {
        toast.success("Tâche exécutée", { description: ev.message });
      } else {
        toast.error("Échec de la tâche", { description: ev.message });
      }
      fetchCron(true);
    } catch (error) {
      toast.error("Erreur", { description: error.response?.data?.detail || "Erreur inconnue" });
    } finally {
      setRunningJobId(null);
    }
  };

  const createCronJob = async (data) => {
    try {
      await axios.post(`${API}/admin/cron/jobs`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Tâche créée");
      fetchCron(true);
      return true;
    } catch (error) {
      toast.error("Erreur", { description: error.response?.data?.detail || "Erreur inconnue" });
      return false;
    }
  };

  const deleteCronJob = async (jobId) => {
    try {
      await axios.delete(`${API}/admin/cron/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Tâche supprimée");
      fetchCron(true);
      return true;
    } catch (error) {
      toast.error("Erreur", { description: error.response?.data?.detail || "Erreur inconnue" });
      return false;
    }
  };

  // Test d'une requête HTTP sans créer la tâche. Retourne le résultat brut.
  const testCronJob = async (data) => {
    try {
      const res = await axios.post(`${API}/admin/cron/test`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    } catch (error) {
      return { status: "error", message: error.response?.data?.detail || "Erreur inconnue" };
    }
  };

  // Aperçu des prochaines exécutions pour un planning donné.
  const previewCronSchedule = async (schedule) => {
    try {
      const res = await axios.post(`${API}/admin/cron/preview`, schedule, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data.next_runs || [];
    } catch (error) {
      return { error: error.response?.data?.detail || "Planning invalide" };
    }
  };

  const clearCronEvents = async () => {
    try {
      await axios.delete(`${API}/admin/cron/events`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Historique vidé");
      fetchCron(true);
    } catch (error) {
      toast.error("Erreur", { description: error.response?.data?.detail || "Erreur inconnue" });
    }
  };

  const revokeSession = async (sessionId) => {
    try {
      await axios.delete(`${API}/admin/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Session révoquée");
      fetchSessions();
    } catch (error) {
      toast.error("Erreur", { description: error.response?.data?.detail || "Erreur inconnue" });
    }
  };

  const revokeAllSessions = async () => {
    try {
      await axios.delete(`${API}/admin/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Toutes les autres sessions ont été révoquées");
      fetchSessions();
    } catch (error) {
      toast.error("Erreur", { description: error.response?.data?.detail || "Erreur inconnue" });
    }
  };

  const createUser = async (username, password, role) => {
    try {
      await axios.post(`${API}/admin/users`, { username, password, role }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Utilisateur "${username}" créé`);
      fetchUsers();
      fetchUsersStats();
      return true;
    } catch (error) {
      const detail = error.response?.data?.detail || "Erreur inconnue";
      toast.error(detail);
      return false;
    }
  };

  const updateUser = async (userId, updates) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Utilisateur mis à jour");
      fetchUsers();
      fetchUsersStats();
    } catch (error) {
      const detail = error.response?.data?.detail || "Erreur inconnue";
      toast.error(detail);
    }
  };

  const deleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Utilisateur supprimé");
      fetchUsers();
      fetchUsersStats();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Email assignment to users
  const assignEmailToUser = async (userId, emailId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/assign-email`, { email_id: emailId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Email assigné");
      fetchUsers();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'assignation");
      return false;
    }
  };

  const unassignEmailFromUser = async (userId, emailId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/unassign-email`, { email_id: emailId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Email retiré");
      fetchUsers();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du retrait");
      return false;
    }
  };

  const searchEmailsForAssign = async (query) => {
    try {
      const params = { limit: 20 };
      if (query) params.search = query;
      const response = await axios.get(`${API}/admin/emails`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      return response.data.emails || [];
    } catch (error) {
      return [];
    }
  };

  const fetchEmailsByIds = async (emailIds) => {
    if (!emailIds?.length) return [];
    try {
      const response = await axios.post(`${API}/admin/emails/by-ids`, { email_ids: emailIds }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.emails || [];
    } catch {
      return [];
    }
  };

  const addDomain = () => {
    setEditDomains([...editDomains, ""]);
  };

  const removeDomain = (index) => {
    setEditDomains(editDomains.filter((_, i) => i !== index));
  };

  const updateDomain = (index, value) => {
    const updated = [...editDomains];
    updated[index] = value.trim().toLowerCase();
    setEditDomains(updated);
  };

  const copyToClipboard = (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      toast.success("Copié!");
    } catch (error) {
      toast.error("Erreur lors de la copie");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Permanent";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  };

  const formatRemainingTime = (seconds) => {
    if (seconds === -1) return "\u221E";
    if (seconds <= 0) return "Expiré";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const toggleEmailExpand = (emailId) => {
    setExpandedEmail(expandedEmail === emailId ? null : emailId);
  };

  return {
    activeTab, setActiveTab, sidebarOpen, setSidebarOpen,
    stats, emails, isLoading, isCreateModalOpen, setIsCreateModalOpen,
    newEmailLifetime, setNewEmailLifetime, customAddress, setCustomAddress,
    isCreating, expandedEmail, toggleEmailExpand, emailSearchQuery, setEmailSearchQuery,
    currentPassword, setCurrentPassword, newUsername, setNewUsername,
    newPassword, setNewPassword, confirmPassword, setConfirmPassword,
    showCurrentPassword, setShowCurrentPassword, showNewPassword, setShowNewPassword,
    isSaving, adminUsername, adminRole, adminPermissions, adminAvatar, uploadAvatar, deleteAvatar,
    selectedEmail, setSelectedEmail, emailMessages, selectedMessage,
    isMessageModalOpen, setIsMessageModalOpen, isLoadingMessages,
    siteSettings, editSiteName, setEditSiteName, editDurations,
    editDefaultDuration, setEditDefaultDuration,
    editAllowPermanent, setEditAllowPermanent,
    editAllowCustomAddress, setEditAllowCustomAddress,
    editMaintenanceMode, setEditMaintenanceMode,
    editAdminTitle, setEditAdminTitle, isSavingConfig,
    editAccentColor, setEditAccentColor,
    editWelcomeText, setEditWelcomeText,
    editFooterText, setEditFooterText,
    editDomains, setEditDomains,
    sessions, isLoadingSessions, fetchSessions, revokeSession, revokeAllSessions,
    cronJobs, cronEvents, isLoadingCron, runningJobId,
    fetchCron, updateCronJob, runCronJob, clearCronEvents,
    createCronJob, deleteCronJob, testCronJob, previewCronSchedule,
    users, usersStats, isLoadingUsers, fetchUsers, fetchUsersStats, createUser, updateUser, deleteUser,
    assignEmailToUser, unassignEmailFromUser, searchEmailsForAssign, fetchEmailsByIds,
    token,
    fetchStats, fetchEmails, fetchEmailMessages, fetchMessage,
    createEmail, deleteEmail, deleteEmailsBulk, deleteAllEmails,
    extendEmail, expireEmail, togglePinEmail, toggleReplyEmail, openEmailInbox, closeEmailInbox,
    saveCredentials, saveSiteSettings,
    addDuration, removeDuration, updateDuration,
    addDomain, removeDomain, updateDomain,
    copyToClipboard, formatDate, formatTime, formatRemainingTime,
  };
}
